import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import AlertDialog, { AlertDialogProps } from './AlertDialog';

interface MockButtonProps {
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
  'aria-label'?: string;
}

interface MockIconProps {
  name: string;
  'aria-label'?: string;
}

jest.mock('components/styleguide/button/Button', () => ({
  __esModule: true,
  default: ({
    children,
    onClick,
    href,
    target,
    rel,
    className,
    'aria-label': ariaLabel,
  }: MockButtonProps) => {
    if (href) {
      return (
        <a
          href={href}
          target={target}
          rel={rel}
          className={className}
          onClick={onClick}
          aria-label={ariaLabel}
        >
          {children}
        </a>
      );
    }
    return (
      <button className={className} onClick={onClick} aria-label={ariaLabel}>
        {children}
      </button>
    );
  },
}));

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, 'aria-label': ariaLabel }: MockIconProps) => (
    <span data-testid={`icon-${name}`} aria-label={ariaLabel} />
  ),
}));

const baseProps: AlertDialogProps = {
  isOpen: true,
  iconName: 'repeat',
  iconVariant: 'info',
  title: "We're setting up your account",
  message: 'Your plan is being reviewed.',
  primaryText: 'Got it, let me explore',
  onPrimary: jest.fn(),
};

describe('AlertDialog', () => {
  it('renders when isOpen is true', () => {
    render(<AlertDialog {...baseProps} />);
    expect(screen.getByText("We're setting up your account")).toBeInTheDocument();
    expect(screen.getByText('Your plan is being reviewed.')).toBeInTheDocument();
    expect(screen.getByText('Got it, let me explore')).toBeInTheDocument();
  });

  it('does not render when isOpen is false', () => {
    render(<AlertDialog {...baseProps} isOpen={false} />);
    expect(screen.queryByText("We're setting up your account")).not.toBeInTheDocument();
  });

  it('renders the icon name passed in', () => {
    render(<AlertDialog {...baseProps} iconName="refresh-cw" />);
    expect(screen.getByTestId('icon-refresh-cw')).toBeInTheDocument();
  });

  describe('icon variant', () => {
    it('applies the info badge class', () => {
      const { container } = render(<AlertDialog {...baseProps} iconVariant="info" />);
      expect(container.querySelector('.iconBadge-info')).toBeInTheDocument();
    });

    it('applies the success badge class', () => {
      const { container } = render(<AlertDialog {...baseProps} iconVariant="success" />);
      expect(container.querySelector('.iconBadge-success')).toBeInTheDocument();
    });

    it('applies the danger badge class', () => {
      const { container } = render(<AlertDialog {...baseProps} iconVariant="danger" />);
      expect(container.querySelector('.iconBadge-danger')).toBeInTheDocument();
    });
  });

  describe('primary action', () => {
    it('calls onPrimary when the primary CTA is clicked', () => {
      const onPrimary = jest.fn();
      render(<AlertDialog {...baseProps} onPrimary={onPrimary} />);
      fireEvent.click(screen.getByText('Got it, let me explore'));
      expect(onPrimary).toHaveBeenCalledTimes(1);
    });

    it('renders the primary as an anchor when primaryHref is provided', () => {
      render(<AlertDialog {...baseProps} primaryHref="https://example.com/book" />);
      const link = screen.getByText('Got it, let me explore').closest('a');
      expect(link).toHaveAttribute('href', 'https://example.com/book');
      expect(link).toHaveAttribute('target', '_blank');
      expect(link).toHaveAttribute('rel', 'noopener noreferrer');
    });

    it('still calls onPrimary when the anchor primary is clicked', () => {
      const onPrimary = jest.fn();
      render(
        <AlertDialog {...baseProps} onPrimary={onPrimary} primaryHref="https://example.com/book" />
      );
      fireEvent.click(screen.getByText('Got it, let me explore'));
      expect(onPrimary).toHaveBeenCalledTimes(1);
    });
  });

  describe('secondary action', () => {
    it('does not render a secondary link when secondary prop is not provided', () => {
      render(<AlertDialog {...baseProps} />);
      expect(screen.queryByText('Go to the platform')).not.toBeInTheDocument();
    });

    it('renders the secondary link when provided', () => {
      const onClick = jest.fn();
      render(
        <AlertDialog
          {...baseProps}
          secondary={{
            prefixText: 'Already scheduled your session?',
            linkText: 'Go to the platform',
            onClick,
          }}
        />
      );
      expect(screen.getByText('Already scheduled your session?')).toBeInTheDocument();
      expect(screen.getByText('Go to the platform')).toBeInTheDocument();
    });

    it('fires secondary onClick when the link is clicked', () => {
      const onClick = jest.fn();
      render(
        <AlertDialog
          {...baseProps}
          secondary={{ linkText: 'Go to the platform', onClick }}
        />
      );
      fireEvent.click(screen.getByText('Go to the platform'));
      expect(onClick).toHaveBeenCalledTimes(1);
    });
  });

  describe('when dismissible is false (default)', () => {
    it('does not render a close button', () => {
      render(<AlertDialog {...baseProps} />);
      expect(screen.queryByLabelText('Close dialog')).not.toBeInTheDocument();
    });

    it('does not call onPrimary when Escape is pressed', () => {
      const onPrimary = jest.fn();
      render(<AlertDialog {...baseProps} onPrimary={onPrimary} />);
      fireEvent.keyDown(document, { key: 'Escape' });
      expect(onPrimary).not.toHaveBeenCalled();
    });

    it('does not call onPrimary when the backdrop is clicked', () => {
      const onPrimary = jest.fn();
      const { container } = render(<AlertDialog {...baseProps} onPrimary={onPrimary} />);
      const backdrop = container.firstChild as HTMLElement;
      fireEvent.click(backdrop);
      expect(onPrimary).not.toHaveBeenCalled();
    });
  });

  describe('when dismissible is true', () => {
    it('renders a close button', () => {
      render(<AlertDialog {...baseProps} dismissible={true} />);
      expect(screen.getByLabelText('Close dialog')).toBeInTheDocument();
    });

    it('calls onPrimary when the close button is clicked', () => {
      const onPrimary = jest.fn();
      render(<AlertDialog {...baseProps} dismissible={true} onPrimary={onPrimary} />);
      fireEvent.click(screen.getByLabelText('Close dialog'));
      expect(onPrimary).toHaveBeenCalledTimes(1);
    });

    it('calls onPrimary when Escape is pressed', () => {
      const onPrimary = jest.fn();
      render(<AlertDialog {...baseProps} dismissible={true} onPrimary={onPrimary} />);
      fireEvent.keyDown(document, { key: 'Escape' });
      expect(onPrimary).toHaveBeenCalledTimes(1);
    });

    it('calls onPrimary when the backdrop is clicked', () => {
      const onPrimary = jest.fn();
      const { container } = render(
        <AlertDialog {...baseProps} dismissible={true} onPrimary={onPrimary} />
      );
      const backdrop = container.firstChild as HTMLElement;
      fireEvent.click(backdrop);
      expect(onPrimary).toHaveBeenCalledTimes(1);
    });

    it('does not dismiss when clicking inside the card', () => {
      const onPrimary = jest.fn();
      render(<AlertDialog {...baseProps} dismissible={true} onPrimary={onPrimary} />);
      fireEvent.click(screen.getByText("We're setting up your account"));
      expect(onPrimary).not.toHaveBeenCalled();
    });
  });

  describe('accessibility', () => {
    it('has role="dialog"', () => {
      render(<AlertDialog {...baseProps} />);
      expect(screen.getByRole('dialog')).toBeInTheDocument();
    });

    it('has aria-modal="true"', () => {
      render(<AlertDialog {...baseProps} />);
      expect(screen.getByRole('dialog')).toHaveAttribute('aria-modal', 'true');
    });

    it('has aria-labelledby pointing to the title', () => {
      render(<AlertDialog {...baseProps} />);
      const dialog = screen.getByRole('dialog');
      const titleId = dialog.getAttribute('aria-labelledby');
      expect(document.getElementById(titleId!)).toHaveTextContent(
        "We're setting up your account"
      );
    });

    it('has aria-describedby pointing to the message', () => {
      render(<AlertDialog {...baseProps} />);
      const dialog = screen.getByRole('dialog');
      const messageId = dialog.getAttribute('aria-describedby');
      expect(document.getElementById(messageId!)).toHaveTextContent(
        'Your plan is being reviewed.'
      );
    });

    it('locks body scroll when open', () => {
      render(<AlertDialog {...baseProps} />);
      expect(document.body.style.overflow).toBe('hidden');
    });

    it('restores body scroll when closed', () => {
      const { rerender } = render(<AlertDialog {...baseProps} />);
      expect(document.body.style.overflow).toBe('hidden');
      rerender(<AlertDialog {...baseProps} isOpen={false} />);
      expect(document.body.style.overflow).toBe('');
    });
  });

  describe('message rendering', () => {
    it('supports JSX messages', () => {
      render(
        <AlertDialog
          {...baseProps}
          message={<span>Review <strong>pending</strong></span>}
        />
      );
      expect(screen.getByText('pending')).toBeInTheDocument();
    });
  });
});
